import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import type { LeadDto } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import {
  LEAD_STATUSES,
  STATUS_LABELS,
  useAssignees,
  useLead,
  useUpdateLead,
  waReplyHref,
} from '../lib/leads.js';
import { useCan } from '../lib/permissions.js';
import { ReplyClock } from '../components/ReplyClock.js';
import { formatDate, formatDateTime, formatInr } from '../lib/format.js';
import {
  QUOTE_PLAN_PRICES,
  QUOTE_PLANS,
  QUOTE_STATE_LABELS,
  quotePdfHref,
  quoteState,
  useQuotes,
  useRaiseQuote,
  useSendQuote,
} from '../lib/quotes.js';

/* ============================================================================
   /leads/:id
   ========================================================================== */

export function LeadDetailPage() {
  const { id = '' } = useParams();
  const { data, isPending, error } = useLead(id);
  const update = useUpdateLead(id);
  const [note, setNote] = useState('');
  const [losing, setLosing] = useState(false);
  const [lostReason, setLostReason] = useState('');

  if (isPending) return <p className="muted">Loading…</p>;

  if (error) {
    return (
      <>
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'That lead was not found.'}
        </p>
        <Link to="/leads" className="btn btn--ghost">
          Back to leads
        </Link>
      </>
    );
  }

  const { lead, notes, client } = data;
  /* `?? []`, not a destructure, and it is not defensive padding. Nothing in
     this dashboard validates a response, and the two halves do not deploy
     together — Cloudflare Pages published the admin build about twenty-five
     minutes after CI went green on 2026-10-13. So there is a window in which
     this build is live against an API that has never heard of `earlier`, and
     destructuring it would throw on `.length` and white-screen the whole lead
     page over a panel that is usually empty anyway. */
  const earlier = data.earlier ?? [];

  function onAddNote(event: FormEvent) {
    event.preventDefault();
    const body = note.trim();
    if (!body) return;
    update.mutate({ note: body }, { onSuccess: () => setNote('') });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{lead.business}</h1>
          <p className="muted">
            {formatDateTime(lead.createdAt)} · {lead.source} · {lead.page}
          </p>
          {/* The promise, beside when the enquiry arrived — which is what it
              is measured from, and the pairing that makes the figure mean
              something. */}
          <p style={{ margin: '6px 0 0' }}>
            <ReplyClock lead={lead} />
          </p>
        </div>
        <div className="row">
          {/* One action, not two. The API knows whether this lead already
              became a client, so the screen offers the one that will work
              rather than offering both and letting a 409 explain. */}
          {client ? (
            <Link className="btn btn--ghost" to={`/clients/${client.id}`}>
              {client.business} kholiye
            </Link>
          ) : (
            <Link className="btn btn--ghost" to={`/clients/new?leadId=${lead.id}`}>
              Make a client
            </Link>
          )}
          <a className="btn" href={waReplyHref(lead)} target="_blank" rel="noreferrer">
            Reply on WhatsApp
          </a>
        </div>
      </div>

      <div className="stack">
        {/* Before everything else, because it changes how the rest is read.
            The same shop asking twice is the most common thing on this board
            that looks like two customers — and quoting a price that has
            already been quoted is the expensive version of that. Rendered
            only when there is something to say; an "Earlier enquiries: none"
            panel on every lead is a panel people stop seeing. */}
        {earlier.length > 0 ? (
          <section className="card">
            <h2 style={{ fontSize: 18, marginBottom: 8 }}>This number has enquired before</h2>
            <ul className="plain-list">
              {earlier.map((item) => (
                <li key={item.id}>
                  <Link to={`/leads/${item.id}`}>{item.business}</Link>
                  <span className="muted">
                    {' — '}
                    {STATUS_LABELS[item.status]} · {item.plan ?? 'no plan'} ·{' '}
                    {formatDate(item.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="card">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Details</h2>
          <dl className="detail-grid">
            <div>
              <dt>Naam</dt>
              <dd>{lead.name ?? '—'}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>
                <a href={`tel:${lead.phone}`}>{lead.phone}</a>
              </dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{lead.email ?? '—'}</dd>
            </div>
            <div>
              <dt>Plan</dt>
              <dd>{lead.plan ?? 'not chosen'}</dd>
            </div>
            <div>
              <dt>Marketing consent</dt>
              <dd>{lead.marketingConsent ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt>Attribution</dt>
              <dd>
                {[lead.utmSource, lead.utmMedium, lead.utmCampaign].filter(Boolean).join(' / ') ||
                  '—'}
              </dd>
            </div>
            <div>
              <dt>Replied</dt>
              <dd>{lead.firstRepliedAt ? formatDateTime(lead.firstRepliedAt) : 'not yet'}</dd>
            </div>
            {/* Shown only when there is one. A "Lost reason: —" row on a live
                enquiry reads as a decision somebody has already taken. */}
            {lead.lostReason ? (
              <div>
                <dt>Lost because</dt>
                <dd>{lead.lostReason}</dd>
              </div>
            ) : null}
          </dl>

          {lead.message ? (
            <>
              <h3 style={{ fontSize: 15, margin: '16px 0 6px' }}>Message</h3>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{lead.message}</p>
            </>
          ) : null}
        </section>

        <section className="card">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Status</h2>
          <div className="row">
            {LEAD_STATUSES.map((value) => (
              <button
                key={value}
                type="button"
                className={lead.status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
                disabled={update.isPending || lead.status === value}
                onClick={() =>
                  value === 'lost' ? setLosing(true) : update.mutate({ status: value })
                }
              >
                {STATUS_LABELS[value]}
              </button>
            ))}
          </div>

          {/* Lost is the one stage that asks a question first, and the answer
              goes in the SAME request as the status. Two requests means the
              first can land without the second, and then the board carries a
              lost lead with no reason — which is the only thing the field
              exists to prevent. The API refuses a reason on any other status
              for the same reason, so this is not the only guard. */}
          {losing ? (
            <form
              className="stack"
              style={{ marginTop: 12 }}
              onSubmit={(event) => {
                event.preventDefault();
                update.mutate(
                  { status: 'lost', lostReason: lostReason.trim() || null },
                  { onSuccess: () => setLosing(false) },
                );
              }}
            >
              <div className="field">
                <label htmlFor="lostReason">Why was it lost?</label>
                <textarea
                  id="lostReason"
                  value={lostReason}
                  maxLength={500}
                  placeholder="Budget nahi tha · kisi aur se bana liya · jawab nahi aaya"
                  onChange={(event) => setLostReason(event.target.value)}
                />
              </div>
              <div className="row">
                <button className="btn btn--small" type="submit" disabled={update.isPending}>
                  Mark lost
                </button>
                <button
                  className="btn btn--ghost btn--small"
                  type="button"
                  onClick={() => setLosing(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </section>

        <Qualifying lead={lead} />

        <section className="card">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Notes</h2>

          <form className="stack" onSubmit={onAddNote}>
            <div className="field">
              <label htmlFor="note" className="sr-only">
                New note
              </label>
              <textarea
                id="note"
                value={note}
                maxLength={2000}
                placeholder="What was discussed?"
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
            <div className="row">
              <button
                className="btn btn--small"
                type="submit"
                disabled={update.isPending || !note.trim()}
              >
                Add note
              </button>
              {update.isError ? (
                <span className="field__error" role="alert">
                  {update.error instanceof ApiError ? update.error.message : 'Could not save.'}
                </span>
              ) : null}
            </div>
          </form>

          {/* A history, not a column that gets overwritten — which is why
              LeadNote is a relation in the schema. */}
          <div className="stack" style={{ marginTop: 16 }}>
            {notes.length === 0 ? (
              <p className="muted">No notes yet.</p>
            ) : (
              notes.map((item) => (
                <div className="note" key={item.id}>
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{item.body}</p>
                  <p className="note__meta" style={{ margin: 0 }}>
                    {item.authorName ?? 'kisi ne'} · {formatDateTime(item.createdAt)}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>

        <Quotes leadId={id} />

        <Link to="/leads" className="btn btn--ghost">
          Back to leads
        </Link>
      </div>
    </>
  );
}

/* ============================================================================
   What the first conversation turned up, and who is working it.
   ----------------------------------------------------------------------------
   None of this is asked on the public form. Every extra field on a form a
   shop owner fills in on 4G costs enquiries, and the three below are things
   the office learns in the first two minutes of talking to somebody.

   One Save for the three text fields, because they are filled in together
   during that call. The ASSIGNMENT saves on change instead: picking a name
   from a dropdown and then having to press Save as well is how a lead ends up
   assigned on one screen and not in the database.

   Deliberately NOT a reply. Writing down that a shop is a clinic in Dewas is
   not answering them, and assigning it to a colleague is the opposite of
   having dealt with it — `countsAsReply` in the API refuses both, or a team
   could meet a two-hour promise by passing one enquiry around.
   ========================================================================== */

function Qualifying({ lead }: { lead: LeadDto }) {
  const update = useUpdateLead(lead.id);
  const can = useCan();
  const mayEdit = can('leads:edit');
  /* Not fetched at all without the permission: the request would answer 403
     and the screen would render the refusal as though something had broken. */
  const assignees = useAssignees(mayEdit);

  const [city, setCity] = useState(lead.city ?? '');
  const [businessType, setBusinessType] = useState(lead.businessType ?? '');
  const [existingWebsite, setExistingWebsite] = useState(lead.existingWebsite ?? '');

  /* The saved values, so the button can say whether there is anything to save.
     Compared against the TRIMMED input for the same reason the API normalises
     it: a trailing space is not a change worth a request. */
  const dirty =
    city.trim() !== (lead.city ?? '') ||
    businessType.trim() !== (lead.businessType ?? '') ||
    existingWebsite.trim() !== (lead.existingWebsite ?? '');

  function onSave(event: FormEvent) {
    event.preventDefault();
    /* Empty string, not null, and the API turns it into null — one place
       decides what an emptied box means, and it is the same place for every
       caller. */
    update.mutate({
      city: city.trim(),
      businessType: businessType.trim(),
      existingWebsite: existingWebsite.trim(),
    });
  }

  return (
    <section className="card">
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>Qualifying</h2>

      <form className="stack" onSubmit={onSave}>
        <div className="field">
          <label htmlFor="city">City</label>
          <input
            id="city"
            value={city}
            maxLength={80}
            disabled={!mayEdit}
            placeholder="Indore"
            onChange={(event) => setCity(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="businessType">Business type</label>
          <input
            id="businessType"
            value={businessType}
            maxLength={80}
            disabled={!mayEdit}
            placeholder="kirana · clinic · salon · coaching"
            onChange={(event) => setBusinessType(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="existingWebsite">Existing website</label>
          <input
            id="existingWebsite"
            value={existingWebsite}
            maxLength={300}
            disabled={!mayEdit}
            placeholder="facebook.com/theirshop · nothing yet"
            onChange={(event) => setExistingWebsite(event.target.value)}
          />
        </div>

        {mayEdit ? (
          <div className="row">
            <button className="btn btn--small" type="submit" disabled={update.isPending || !dirty}>
              Save details
            </button>
            {update.isError ? (
              <span className="field__error" role="alert">
                {update.error instanceof ApiError ? update.error.message : 'Could not save.'}
              </span>
            ) : null}
          </div>
        ) : null}
      </form>

      <div className="field" style={{ marginTop: 16 }}>
        <label htmlFor="assignedTo">Assigned to</label>
        <select
          id="assignedTo"
          value={lead.assignedToId ?? ''}
          disabled={!mayEdit || update.isPending}
          onChange={(event) => update.mutate({ assignedToId: event.target.value || null })}
        >
          <option value="">Nobody yet</option>
          {/* The current holder is listed even if the endpoint has not
              answered yet, or no longer offers them — a disabled account keeps
              the leads it had, and a `<select>` whose value is not among its
              options silently shows the first one instead, which would read as
              the lead having been reassigned to whoever is at the top. */}
          {lead.assignedToId &&
          !(assignees.data?.items ?? []).some((who) => who.id === lead.assignedToId) ? (
            <option value={lead.assignedToId}>{lead.assignedToName ?? 'Current holder'}</option>
          ) : null}
          {(assignees.data?.items ?? []).map((who) => (
            <option key={who.id} value={who.id}>
              {who.name}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}

/* ============================================================================
   Quotes for this lead.
   ----------------------------------------------------------------------------
   Two steps on purpose: raise it, read the PDF, then send. What goes out is a
   price commitment, and a one-tap "create and send" is a one-tap wrong price
   in a customer's hand — the same reason settling an invoice is two steps.

   The price is typed rather than derived. A quote is exactly where a discount
   happens, and a silently derived price hides it; the plan's list price only
   prefills the field. Who typed it is recorded, and that is the control.
   ========================================================================== */

function Quotes({ leadId }: { leadId: string }) {
  const { data, isPending, error } = useQuotes(leadId);
  const raise = useRaiseQuote(leadId);
  const send = useSendQuote(leadId);

  const [plan, setPlan] = useState<(typeof QUOTE_PLANS)[number]>('business');
  const [amount, setAmount] = useState(String(QUOTE_PLAN_PRICES.business));
  const [notes, setNotes] = useState('');

  function choosePlan(next: (typeof QUOTE_PLANS)[number]) {
    setPlan(next);
    // Prefill, not lock: the field stays editable and whatever is in it is
    // what gets sent.
    setAmount(String(QUOTE_PLAN_PRICES[next]));
  }

  function onRaise(event: FormEvent) {
    event.preventDefault();
    const amountInr = Number.parseInt(amount, 10);
    if (!Number.isFinite(amountInr)) return;
    raise.mutate({ plan, amountInr, notes: notes.trim() }, { onSuccess: () => setNotes('') });
  }

  const quotes = data?.items ?? [];

  return (
    <section className="card">
      <h2 className="section-title">Quotes</h2>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load quotes.'}
        </p>
      ) : null}
      {isPending ? <p className="muted">Loading…</p> : null}

      {!isPending && quotes.length === 0 ? (
        <p className="muted" style={{ margin: '0 0 12px' }}>
          No quote sent yet.
        </p>
      ) : null}

      <div className="stack" style={{ marginBottom: 16 }}>
        {quotes.map((quote) => {
          const state = quoteState(quote);
          return (
            <div className="lead-card" key={quote.id}>
              <div className="lead-card__business">
                {quote.number} · {formatInr(quote.grossInr)}
              </div>
              <div className="lead-card__meta">
                {quote.plan} · {formatInr(quote.amountInr)} + {quote.gstPercent}% GST ·{' '}
                <span className={state === 'expired' ? 'pill pill--warn' : 'pill'}>
                  {QUOTE_STATE_LABELS[state]}
                </span>
              </div>
              <div className="lead-card__meta">
                {quote.expired ? 'Was valid until' : 'Valid until'} {formatDate(quote.validUntil)}
                {quote.createdByName ? ` · ${quote.createdByName}` : ''}
                {quote.sentAt ? ` · sent ${formatDateTime(quote.sentAt)}` : ''}
              </div>

              <div className="row" style={{ marginTop: 8 }}>
                {/* A plain link, not a fetch. The PDF is behind the session
                    cookie and opens in the browser's own viewer — which is the
                    preview that makes sending a separate step worth having. */}
                <a
                  className="btn btn--ghost btn--small"
                  href={quotePdfHref(quote.id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open PDF
                </a>
                {/* Absent once expired rather than present and refused: the API
                    answers 409, and a button that exists only to say no is one
                    somebody presses first and reads second. */}
                {quote.expired ? null : (
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    disabled={send.isPending}
                    onClick={() => send.mutate(quote.id)}
                  >
                    {quote.sentAt ? 'Send again' : 'Send on WhatsApp'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {send.error ? (
        <p className="alert" role="alert">
          {send.error instanceof ApiError ? send.error.message : 'Could not send it.'}
        </p>
      ) : null}

      <form className="stack" onSubmit={onRaise} noValidate>
        <h3 style={{ fontSize: 16, margin: 0 }}>New quote</h3>

        <div className="field">
          <label htmlFor="quotePlan">Plan</label>
          <select
            id="quotePlan"
            value={plan}
            onChange={(event) => choosePlan(event.target.value as (typeof QUOTE_PLANS)[number])}
          >
            {QUOTE_PLANS.map((value) => (
              <option key={value} value={value}>
                {value} · {formatInr(QUOTE_PLAN_PRICES[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="quoteAmount">Amount (GST extra)</label>
          <input
            id="quoteAmount"
            inputMode="numeric"
            value={amount}
            onChange={(event) => setAmount(event.target.value.replace(/\D/g, ''))}
          />
          <span className="muted" style={{ fontSize: 13 }}>
            The list price is filled in. Change it here to give a discount — whatever is typed here
            is what goes on the quote.
          </span>
          {raise.error instanceof ApiError && raise.error.fields.amountInr ? (
            <span className="field__error" role="alert">
              {raise.error.fields.amountInr}
            </span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="quoteNotes">What is included</label>
          <textarea
            id="quoteNotes"
            value={notes}
            maxLength={1000}
            placeholder="One-page website, 5 sections, WhatsApp button…"
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>

        {raise.error instanceof ApiError && Object.keys(raise.error.fields).length === 0 ? (
          <p className="alert" role="alert">
            {raise.error.message}
          </p>
        ) : null}

        <div>
          <button className="btn" type="submit" disabled={raise.isPending || !amount}>
            {raise.isPending ? 'Creating…' : 'Create quote'}
          </button>
        </div>
      </form>
    </section>
  );
}
